import { useEffect, useRef, useState } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { Cancel01Icon, Search01Icon, Add01Icon } from '@hugeicons/core-free-icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

/**
 * IntegrationTags — Displays integration tags with autocomplete picker.
 * Searches against the integration catalog API and allows creating new entries.
 *
 * @param {object} props
 * @param {string[]} props.integrations - Current integration names
 * @param {(integrations: string[]) => Promise<void>} props.onSave - Callback to persist changes
 * @param {boolean} [props.readOnly] - Disable editing
 */
export function IntegrationTags({ integrations = [], onSave, readOnly = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (query.length < 1) {
      setResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/integraciones', { params: { q: query } });
        // Filter out already-selected integrations
        const filtered = (data || []).filter(
          (item) => !integrations.some((i) => i.toLowerCase() === item.name.toLowerCase()),
        );
        setResults(filtered);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, integrations]);

  const addIntegration = async (name) => {
    const updated = [...integrations, name];
    setSaving(true);
    try {
      await onSave(updated);
      setOpen(false);
      setQuery('');
      setResults([]);
    } catch (e) {
      toast.error('Error al guardar integración');
    } finally {
      setSaving(false);
    }
  };

  const removeIntegration = async (name) => {
    const updated = integrations.filter((i) => i !== name);
    setSaving(true);
    try {
      await onSave(updated);
    } catch {
      toast.error('Error al quitar integración');
    } finally {
      setSaving(false);
    }
  };

  const createAndAdd = async (name) => {
    try {
      await api.post('/integraciones', { name });
      await addIntegration(name);
    } catch {
      toast.error('Error al crear integración');
    }
  };

  const trimmedQuery = query.trim();
  const queryExistsInResults = results.some((r) => r.name.toLowerCase() === trimmedQuery.toLowerCase());
  const queryAlreadySelected = integrations.some((i) => i.toLowerCase() === trimmedQuery.toLowerCase());
  const showCreateOption = trimmedQuery.length >= 2 && !queryExistsInResults && !queryAlreadySelected;

  return (
    <div className="space-y-3">
      {/* Tags */}
      <div className="flex flex-wrap gap-2">
        {integrations.map((name) => (
          <Badge
            key={name}
            variant="outline"
            className="text-xs gap-1.5 pl-2.5 pr-1.5 py-1 bg-muted/50 border-border hover:border-primary/30 transition-colors">
            <span>{name}</span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => removeIntegration(name)}
                disabled={saving}
                className="text-muted-foreground hover:text-destructive transition-colors rounded-sm"
                aria-label={`Quitar integración ${name}`}>
                <HugeiconsIcon icon={Cancel01Icon} className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}

        {/* Add button / trigger */}
        {!readOnly && (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border border-dashed border-border px-2.5 py-1',
                  'text-xs text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors cursor-pointer',
                )}
                aria-label="Agregar integración">
                <HugeiconsIcon icon={Add01Icon} className="h-3 w-3" />
                <span>Agregar</span>
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0" align="start">
              <Command shouldFilter={false}>
                <div className="flex items-center border-b px-3">
                  <HugeiconsIcon icon={Search01Icon} className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                  <input
                    className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                    placeholder="Buscar integración..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && showCreateOption) {
                        e.preventDefault();
                        createAndAdd(trimmedQuery);
                      }
                    }}
                    aria-label="Buscar integración"
                    autoFocus
                  />
                </div>
                <CommandList>
                  {loading && <div className="py-4 text-center text-xs text-muted-foreground">Buscando...</div>}
                  {!loading && query.length >= 1 && results.length === 0 && !showCreateOption && (
                    <CommandEmpty>No se encontraron integraciones</CommandEmpty>
                  )}
                  {results.length > 0 && (
                    <CommandGroup>
                      {results.map((item) => (
                        <CommandItem
                          key={item.id}
                          value={item.id}
                          onSelect={() => addIntegration(item.name)}
                          className="cursor-pointer text-sm">
                          {item.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                  {showCreateOption && (
                    <CommandGroup>
                      <CommandItem onSelect={() => createAndAdd(trimmedQuery)} className="cursor-pointer text-sm gap-2">
                        <HugeiconsIcon icon={Add01Icon} className="h-3.5 w-3.5 text-primary" />
                        <span>
                          Crear: "<span className="font-medium">{trimmedQuery}</span>"
                        </span>
                      </CommandItem>
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Empty state */}
      {integrations.length === 0 && readOnly && (
        <p className="text-sm text-muted-foreground">Sin integraciones registradas</p>
      )}
    </div>
  );
}
